// wiswiz 752 (overflow on diverse material, Philip orders 2380/2381): steps hold prose written as display math, also
// inside \begin{aligned} and mixed with small formulas ("\text{Groep I:} &\quad \mu_1 \approx 8\,\text{s}, ... (\text{smalle
// top})"). Display math cannot wrap, so these lines ran 340-690 px wide in a 325 px step page (cut). The prose lines of a
// display formula (15+ characters of top-level \text) become paragraphs: the \text pieces as text in the page font, the
// formula pieces between them as inline math, spacing and punctuation as text. Its other lines stay display math, kept
// together (and aligned when they were). A formula without a prose line stays as it is.
const TEXT = /\\text\{((?:[^{}]|\{[^{}]*\})*)\}/g;
const SPACING = /\\(?:quad|qquad|[,;:! ])|~/g;
const ARROW = /\\(?:Longr|R)ightarrow/g;

/** The \text pieces of a line that stand at brace depth 0 (a \text inside \frac{..}{..} belongs to its formula). */
function topLevelTexts(line: string): RegExpMatchArray[] | null {
  const found: RegExpMatchArray[] = [];
  for (const m of line.matchAll(TEXT)) {
    let depth = 0;
    for (let i = 0; i < (m.index ?? 0); i++) {
      if (line[i] === "\\") i++;
      else if (line[i] === "{") depth++;
      else if (line[i] === "}") depth--;
    }
    if (depth !== 0) return null;
    found.push(m);
  }
  return found;
}

function proseParagraph(line: string): string | null {
  const texts = topLevelTexts(line);
  if (!texts || texts.map((m) => m[1]).join("").replace(/\\[,;:! ]/g, "").trim().length < 15) return null;
  const pieces: string[] = [];
  let at = 0;
  const math = (seg: string): boolean => {
    const arrows = seg.replace(ARROW, " ⇒ ");
    const bare = arrows.replace(SPACING, " ");
    if (!/\S/.test(bare)) return !!pieces.push(" ");
    if (/^[\s,.;:()⇒]*$/.test(bare)) return !!pieces.push(bare);
    if (/\\(?:Longr|R)ightarrow/.test(seg) || /[{}]/.test(seg.replace(/\{[^{}]*\}/g, ""))) return false;
    // spacing and punctuation at the ends of a formula piece stand as text around it
    const m = arrows.match(/^((?:\s|\\(?:quad|qquad|[,;:! ])|~|[,.;:])*)([\s\S]*?)((?:\s|\\(?:quad|qquad|[,;:! ])|~|[,.;:])*)$/);
    pieces.push(`${(m?.[1] ?? "").replace(SPACING, " ")} $${(m?.[2] ?? seg).trim()}$ ${(m?.[3] ?? "").replace(SPACING, " ")}`);
    return true;
  };
  for (const m of texts) {
    if (!math(line.slice(at, m.index))) return null;
    pieces.push(m[1].replace(SPACING, " "));
    at = (m.index ?? 0) + m[0].length;
  }
  if (!math(line.slice(at))) return null;
  return pieces
    .join("")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.;:)])/g, "$1")
    .replace(/\(\s+/g, "(")
    .trim();
}

export function proseMathLines(expr: string): string | null {
  const whole = expr.trim();
  const env = whole.match(/^\\begin\{(aligned|gathered)\}([\s\S]*)\\end\{\1\}$/);
  const body = env ? env[2] : whole;
  if (/\\begin\{/.test(body)) return null;
  const lines = body
    .split(/\\\\(?:\[[^\]]*\])?/)
    .map((l) => l.trim())
    .filter(Boolean);
  const parts: ({ prose: string } | { math: string })[] = [];
  for (const line of lines) {
    const prose = proseParagraph(env ? line.replace(/&/g, " ") : line);
    if (prose) parts.push({ prose });
    else {
      const last = parts[parts.length - 1];
      if (env && last && "math" in last) last.math += ` \\\\ ${line}`;
      else parts.push({ math: line });
    }
  }
  if (!parts.some((p) => "prose" in p)) return null;
  const out = parts.map((p) =>
    "prose" in p ? p.prose : `$$\n${env ? `\\begin{${env[1]}}${p.math}\\end{${env[1]}}` : p.math}\n$$`
  );
  return `\n\n${out.join("\n\n")}\n\n`;
}
